import {throttle} from '@github/mini-throttle'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {type InvalidateQueryFilters, type QueryKey, useQueryClient} from '@github-ui/react-query'
import {useAlive} from '@github-ui/use-alive'
import useIsMounted from '@github-ui/use-is-mounted'
import {useInvalidateOnAliveUpdate} from '@github-ui/use-invalidate-on-alive-update'
import {useMemo, useState, useEffect} from 'react'
import {usePageVisibility} from './use-page-visibility'

import type {AliveChannels as MergeBoxAliveChannels} from '../page-data/payloads/merge-box'
import type {AliveChannels as StatusChecksAliveChannels} from '../page-data/payloads/status-checks'

// Keys are derived from the real payload types, so adding a channel to either
// payload automatically requires a matching `useAlive` subscription below.
// Values are loosened to nullable because callers build channel objects whose
// individual channels may be absent at runtime.
export type MergeabilityLiveUpdateChannels = {
  [K in keyof MergeBoxAliveChannels]: string | null | undefined
} & {
  [K in keyof StatusChecksAliveChannels]?: string | null | undefined
}

// Stable fallback so consuming hooks aren't handed a fresh object every render.
export const EMPTY_CHANNELS: MergeabilityLiveUpdateChannels = {
  stateChannel: null,
  deployedChannel: null,
  reviewStateChannel: null,
  workflowsChannel: null,
  mergeQueueChannel: null,
  headRefChannel: null,
  baseRefChannel: null,
  gitMergeStateChannel: null,
  pullRequestChannel: null,
  codeScanningChannel: null,
  commitHeadShaChannel: null,
  proofOfPresenceChannel: null,
} as const

const emptyQueryKey = [] as const

export function useMergeabilityLiveUpdates({
  channels,
  refetchQuery,
  queryKey = emptyQueryKey,
  throttleTime = 2000,
}: {
  channels: MergeabilityLiveUpdateChannels | null
  refetchQuery: () => void
  queryKey?: QueryKey | InvalidateQueryFilters
  throttleTime?: number
}): void {
  const isMounted = useIsMounted()
  const [hasPendingUpdate, setHasPendingUpdate] = useState(false)
  const isPageVisible = usePageVisibility()
  const queryClient = useQueryClient()

  const dedupeConcurrentRefetches = isFeatureEnabled('mergeability_refetch_dedupe')

  // There are several PR websocket events that can trigger a refresh of the
  // MergeBox Pull Request data.
  // This creates a throttle (default 2s) to prevent multiple refetches
  // happening in the same time window
  // This is also in place to preventing inflicting a DDOS attack on ourselves
  // by refetching too often.
  const throttleRefetchingPullRequestData = useMemo(
    () =>
      throttle(() => {
        if (isMounted()) {
          refetchQuery()
        }
      }, throttleTime),
    [refetchQuery, isMounted, throttleTime],
  )

  const handleMergabilityChannelNotification = useMemo(
    () => (data: {wait?: number}) => {
      if (isPageVisible) {
        // Some of our WebSocket event notifications include a wait value to assure that we consider replication lag times.
        // We need to avoid calling our refetching method until that wait time is over by using a setTimeout that is equal to the data.wait value, if it exists.
        window.setTimeout(() => throttleRefetchingPullRequestData(), data.wait || 0)
      } else {
        setHasPendingUpdate(true)
      }
    },
    [isPageVisible, throttleRefetchingPullRequestData],
  )

  const invalidateConfig = useMemo(() => ({throttleTimeout: throttleTime}), [throttleTime])

  useEffect(() => {
    if (isMounted() && isPageVisible && hasPendingUpdate) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHasPendingUpdate(false)
      if (dedupeConcurrentRefetches) {
        return // we don't need to do this for the invalidating approach because TSQ automatically waits to refetch invalidated queries until the window is active
      } else {
        refetchQuery()
      }
    }
  }, [
    isPageVisible,
    hasPendingUpdate,
    setHasPendingUpdate,
    isMounted,
    refetchQuery,
    dedupeConcurrentRefetches,
    queryClient,
  ])

  // Subscribe to every channel. Calling useAlive as object-literal values keeps
  // hook order stable (properties evaluate unconditionally, in source order),
  // while `satisfies Record<keyof ...>` makes it a compile error to add a new
  // channel key without also subscribing to it here.
  //
  // Original refetch path (flag OFF); a nullish channel no-ops the subscription when ON.
  const refetchChannels = dedupeConcurrentRefetches ? null : channels
  ;({
    stateChannel: useAlive(refetchChannels?.stateChannel, handleMergabilityChannelNotification),
    deployedChannel: useAlive(refetchChannels?.deployedChannel, handleMergabilityChannelNotification),
    reviewStateChannel: useAlive(refetchChannels?.reviewStateChannel, handleMergabilityChannelNotification),
    workflowsChannel: useAlive(refetchChannels?.workflowsChannel, handleMergabilityChannelNotification),
    mergeQueueChannel: useAlive(refetchChannels?.mergeQueueChannel, handleMergabilityChannelNotification),
    headRefChannel: useAlive(refetchChannels?.headRefChannel, handleMergabilityChannelNotification),
    baseRefChannel: useAlive(refetchChannels?.baseRefChannel, handleMergabilityChannelNotification),
    gitMergeStateChannel: useAlive(refetchChannels?.gitMergeStateChannel, handleMergabilityChannelNotification),
    pullRequestChannel: useAlive(refetchChannels?.pullRequestChannel, handleMergabilityChannelNotification),
    codeScanningChannel: useAlive(refetchChannels?.codeScanningChannel, handleMergabilityChannelNotification),
    commitHeadShaChannel: useAlive(refetchChannels?.commitHeadShaChannel, handleMergabilityChannelNotification),
    proofOfPresenceChannel: useAlive(refetchChannels?.proofOfPresenceChannel, handleMergabilityChannelNotification),
  }) satisfies Record<keyof MergeabilityLiveUpdateChannels, void>

  // Additive dedupe path (flag ON); a nullish channel no-ops the subscription when OFF. Every channel shares the same
  // query key, so simultaneous events across channels collapse into a single invalidation.
  const invalidateChannels = dedupeConcurrentRefetches && queryKey !== emptyQueryKey ? channels : null
  ;({
    stateChannel: useInvalidateOnAliveUpdate(invalidateChannels?.stateChannel, queryKey, invalidateConfig),
    deployedChannel: useInvalidateOnAliveUpdate(invalidateChannels?.deployedChannel, queryKey, invalidateConfig),
    reviewStateChannel: useInvalidateOnAliveUpdate(invalidateChannels?.reviewStateChannel, queryKey, invalidateConfig), // prettier-ignore
    workflowsChannel: useInvalidateOnAliveUpdate(invalidateChannels?.workflowsChannel, queryKey, invalidateConfig),
    mergeQueueChannel: useInvalidateOnAliveUpdate(invalidateChannels?.mergeQueueChannel, queryKey, invalidateConfig), // prettier-ignore
    headRefChannel: useInvalidateOnAliveUpdate(invalidateChannels?.headRefChannel, queryKey, invalidateConfig),
    baseRefChannel: useInvalidateOnAliveUpdate(invalidateChannels?.baseRefChannel, queryKey, invalidateConfig),
    gitMergeStateChannel: useInvalidateOnAliveUpdate(invalidateChannels?.gitMergeStateChannel, queryKey, invalidateConfig), // prettier-ignore
    pullRequestChannel: useInvalidateOnAliveUpdate(invalidateChannels?.pullRequestChannel, queryKey, invalidateConfig), // prettier-ignore
    codeScanningChannel: useInvalidateOnAliveUpdate(invalidateChannels?.codeScanningChannel, queryKey, invalidateConfig), // prettier-ignore
    commitHeadShaChannel: useInvalidateOnAliveUpdate(invalidateChannels?.commitHeadShaChannel, queryKey, invalidateConfig), // prettier-ignore
    proofOfPresenceChannel: useInvalidateOnAliveUpdate(invalidateChannels?.proofOfPresenceChannel, queryKey, invalidateConfig), // prettier-ignore
  }) satisfies Record<keyof MergeabilityLiveUpdateChannels, void>
}
